// ============================================================
// API Service Layer — IT Project Portal
// All calls go through /api/* (proxied to Azure Functions)
// ============================================================

const BASE = '/api';

// ── Session expired callback ──────────────────────────────────
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(fn: () => void) {
  onSessionExpired = fn;
}

// ── Bearer token provider ─────────────────────────────────────
// useAuth registers a getter so fetchJson can attach the MSAL token to every request.
let getAccessToken: (() => Promise<string | null>) | null = null;
export function setTokenProvider(fn: () => Promise<string | null>) {
  getAccessToken = fn;
}

// ── Core fetch wrapper ────────────────────────────────────────
async function fetchJson<T>(path: string, options?: RequestInit, retries = 3): Promise<T> {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // Attach MSAL token as X-User-Token — SWA strips the Authorization header
      // before it reaches Azure Functions, so we use a custom header instead.
      const token = getAccessToken ? await getAccessToken().catch(() => null) : null;
      const authHeaders: Record<string, string> = token
        ? { 'X-User-Token': token }
        : {};
      const res = await fetch(`${BASE}${path}`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        ...options,
      });
      if (res.status === 401 && !path.startsWith('/mgmt/')) {
        onSessionExpired?.();
        throw new Error('Session expired — please refresh the page');
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      return res.json();
    } catch (err: any) {
      if (attempt === retries) throw err;
      if (err.message?.includes('Session expired')) throw err;
      const delay = attempt * 5000;
      console.warn(`[api] Attempt ${attempt} failed for ${path}, retrying in ${delay / 1000}s...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw new Error('Request failed after retries');
}

// ── Types ─────────────────────────────────────────────────────

export interface Project {
  ProjectID:              number;
  Title:                  string;
  DepartmentID:           number;
  DepartmentName:         string;
  Priority:               string;
  Budgeted:               'Yes' | 'No' | 'TBD';
  AmountBudgeted:         number | null;
  StartDate:              string;
  EndDate:                string;
  RequestorUserID:        number;
  RequestorName:          string;
  RequestorEmail:         string;
  BusinessOwnerName:      string | null;
  BusinessOwnerEmail:     string | null;
  ProjectManagerName:     string | null;
  ProjectManagerEmail:    string | null;
  DirectorName:           string | null;
  DirectorEmail:          string | null;
  AGMName:                string | null;
  AGMEmail:               string | null;
  ApprovalStatus:         ApprovalStatus;
  ProjectStatus:          ProjectStatus;
  ITNotes:                string | null;
  ProjectDescription:     string | null;
  Objectives:             string | null;
  Deliverables:           string | null;
  OrganizationalBenefits: string | null;
  AssignedToUserID:       number | null;
  AssignedToName:         string | null;
  EstimatedTimeline:      string | null;
  EstimatedBudget:        number | null;
  IsDraft:                boolean;
  RevisionComment:        string | null;
  IsActive:               boolean;
  CreatedAt:              string;
  UpdatedAt:              string;
  SubmittedAt:            string | null;
  LastDraftSavedAt:       string | null;
}

export interface ProjectListResponse {
  rows:     Project[];
  total:    number;
  page:     number;
  pageSize: number;
}

export interface ProjectDetail extends Project {
  approvals:   Approval[];
  comments:    Comment[];
  attachments: Attachment[];
}

export interface Approval {
  ApprovalID:      number;
  ProjectID:       number;
  StepNumber:      number;
  StepName:        string;
  ApproverRole:    string;
  ApproverEmail:   string | null;
  ApproverName:    string | null;
  Status:          'Pending' | 'Approved' | 'Denied' | 'Skipped' | 'Revision' | 'Paused';
  ActionTakenAt:   string | null;
  Comments:        string | null;
  CreatedAt:       string;
  UpdatedAt:       string;
  Title?:              string;
  Priority?:           string;
  RequestorName?:      string;
  DepartmentName?:     string;
  ProjectDescription?: string;
  Budgeted?:           string;
  StartDate?:          string;
  EndDate?:            string;
  EstimatedTimeline?:  string;
  EstimatedBudget?:    number;
}

export interface Comment {
  CommentID:      number;
  ProjectID:      number;
  AuthorUserID:   number;
  AuthorName:     string;
  CommentText:    string;
  IsInternal:     boolean;
  IsQuestion:     boolean;
  QuestionStatus: 'Open' | 'Answered' | null;
  IsDeleted:      boolean;
  CreatedAt:      string;
  UpdatedAt:      string;
}

export interface Attachment {
  AttachmentID:     number;
  ProjectID:        number;
  FileName:         string;
  BlobUrl:          string;
  FileSizeBytes:    number;
  MimeType:         string | null;
  UploadedByName:   string;
  UploadedAt:       string;
}

export interface Department {
  DepartmentID:   number;
  DepartmentName: string;
  DirectorName:   string | null;
  DirectorEmail:  string | null;
  AGMName:        string | null;
  AGMEmail:       string | null;
  IsActive:       boolean;
  CreatedAt:      string;
  UpdatedAt:      string;
}

export interface User {
  UserID:         number;
  EntraObjectID:  string;
  Email:          string;
  DisplayName:    string;
  AppRole:        AppRole | null;
  DepartmentID:   number | null;
  DepartmentName: string | null;
  IsActive:       boolean;
  LastLoginAt:    string | null;
  CreatedAt:      string;
}

export interface Notification {
  NotificationID:   number;
  Title:            string;
  Message:          string;
  LinkUrl:          string | null;
  IsRead:           boolean;
  CreatedAt:        string;
  RelatedProjectID: number | null;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unreadCount:   number;
}

export interface FormFieldSetting {
  FieldKey:   string;
  Label:      string;
  HelpText:   string | null;
  IsVisible:  boolean;
  IsRequired: boolean;
  IsEnabled:  boolean;
  SortOrder:  number;
}

export interface PortalSettings {
  RequirementsTrackerVisible: string;
  MasqueradeTimeoutMinutes:   string;
  MaintenanceModeEnabled:     string;
  MaxAttachmentSizeMB:        string;
  AllowedAttachmentTypes:     string;
  UATTestingVisible:          string;
}

export interface AdminStats {
  TotalUsers:       number;
  TotalProjects:    number;
  ActiveProjects:   number;
  PendingApprovals: number;
  DraftProjects:    number;
  RevisionProjects: number;
  LastAuditEvent:   string | null;
}

export interface ActivityItem {
  AuditID:      number;
  Action:       string;
  ActorName:    string;
  CreatedAt:    string;
  ProjectID:    number | null;
  ProjectTitle: string | null;
  EntityType:   string | null;
}

export interface ScopeItem {
  ScopeItemID:    number;
  ProjectID:      number;
  Category:       string | null;
  ItemOrder:      number;
  Question:       string;
  VendorResponse: string | null;
  InternalNotes:  string | null;
  Status:         'Open' | 'Resolved' | 'Blocked' | 'N/A';
  IsRequired:     boolean;
  AddedByName:    string | null;
  ResolvedAt:     string | null;
  CreatedAt:      string;
  UpdatedAt:      string;
  IsDeleted:      boolean;
}

export interface ApprovalChainStep {
  StepID:      number;
  StepName:    string;
  StepOrder:   number;
  IsEnabled:   boolean;
  UpdatedAt:   string;
  UpdatedByID: number | null;
}

export interface LoginHistoryEntry {
  AuditID:    number;
  ActorEmail: string;
  ActorName:  string;
  Action:     'User.Login' | 'User.Logout';
  CreatedAt:  string;
}

export interface HealthStatus {
  status:    'ok' | 'degraded';
  dbLatency: number;
  timestamp: string;
  error?:    string;
}

export interface UATSession {
  SessionID:       number;
  TesterName:      string;
  TesterEmail:     string;
  TesterRole:      string;
  Track:           'user' | 'admin';
  Status:          'in_progress' | 'submitted';
  OverallRating?:  number;
  OverallComments?: string;
  StartedAt:       string;
  SubmittedAt?:    string;
}

export interface UATResponse {
  ResponseID:    number;
  SessionID:     number;
  ScenarioID:    string;
  ScenarioTitle: string;
  Result:        'pass' | 'fail' | 'partial' | 'skipped';
  Rating?:       number;
  Comments?:     string;
  TimeSpentSec?: number;
  CreatedAt:     string;
}

export type AppRole = 'IT.Admin' | 'IT.Approver' | 'Director' | 'EMT' | null;

export type ApprovalStatus =
  | 'Draft'
  | 'Director Review' | 'Director Denied'
  | 'EMT Review'      | 'EMT Denied'
  | 'AGM Review'      | 'AGM Denied'
  | 'IT Review'       | 'IT Denied'
  | 'CEO Review'      | 'CEO Denied'
  | 'Revision'        | 'Paused'
  | 'Approved'        | 'Denied';

export type ProjectStatus =
  | 'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled';

export interface ProjectListParams {
  sort?:   string;
  dir?:    'asc' | 'desc';
  page?:   number;
  size?:   number;
  status?: string;
  search?: string;
}

// ── API ───────────────────────────────────────────────────────

export const api = {

  health: {
    check: () => fetchJson<HealthStatus>('/health'),
  },

  projects: {
    list: (params?: ProjectListParams) => {
      const qs = new URLSearchParams();
      if (params?.sort)   qs.set('sort',   params.sort);
      if (params?.dir)    qs.set('dir',    params.dir);
      if (params?.page)   qs.set('page',   String(params.page));
      if (params?.size)   qs.set('size',   String(params.size));
      if (params?.status) qs.set('status', params.status);
      if (params?.search) qs.set('search', params.search);
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return fetchJson<ProjectListResponse>(`/projects${query}`).then(r => r.rows);
    },
    listPaged: (params?: ProjectListParams) => {
      const qs = new URLSearchParams();
      if (params?.sort)   qs.set('sort',   params.sort);
      if (params?.dir)    qs.set('dir',    params.dir);
      if (params?.page)   qs.set('page',   String(params.page));
      if (params?.size)   qs.set('size',   String(params.size));
      if (params?.status) qs.set('status', params.status);
      if (params?.search) qs.set('search', params.search);
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return fetchJson<ProjectListResponse>(`/projects${query}`);
    },
    get:      (id: number) => fetchJson<ProjectDetail>(`/projects/${id}`),
    myDrafts: () => fetchJson<Project[]>('/projects/my-drafts'),
    deleteDraft: (id: number) => fetchJson<{ message: string }>(`/projects/${id}/draft`, { method: 'DELETE' }),
    create:   (body: object) => fetchJson<{ projectID: number; isDraft?: boolean; message: string }>(
      '/projects', { method: 'POST', body: JSON.stringify(body) }
    ),
    saveDraft: (body: object) => fetchJson<{ projectID: number; isDraft: boolean; message: string }>(
      '/projects', { method: 'POST', body: JSON.stringify({ ...body, isDraft: true }) }
    ),
    resubmit: (id: number, body: object) => fetchJson<{ message: string; newStatus: string }>(
      `/projects/${id}/resubmit`, { method: 'POST', body: JSON.stringify(body) }
    ),
  },

  attachments: {
    list: (projectId: number) => fetchJson<Attachment[]>(`/projects/${projectId}/attachments`),
    getSasToken: (projectId: number, fileName: string) =>
      fetchJson<{ blobUrl: string; blobName: string; expiresOn: string }>(
        `/projects/${projectId}/attachments/sas-token?fileName=${encodeURIComponent(fileName)}`
      ),
    create: (projectId: number, body: object) =>
      fetchJson<{ attachmentID: number }>(`/projects/${projectId}/attachments`, {
        method: 'POST', body: JSON.stringify(body),
      }),
    delete: (projectId: number, attachmentId: number) =>
      fetchJson<{ blobName: string; message: string }>(
        `/projects/${projectId}/attachments/${attachmentId}`, { method: 'DELETE' }
      ),
    getDownloadUrl: (projectId: number, attachmentId: number) =>
      fetchJson<{ url: string }>(
        `/projects/${projectId}/attachments/${attachmentId}/download`
      ),
  },

  approvals: {
    pending: (email: string) =>
      fetchJson<Approval[]>(`/approvals/pending?email=${encodeURIComponent(email)}`),
    action: (approvalId: number, body: {
      action:        'Approved' | 'Denied' | 'Revision' | 'Paused';
      comments?:     string | null;
      approverEmail: string;
      approverName:  string;
    }) => fetchJson<{ message: string; newStatus: string }>(
      `/approvals/${approvalId}/action`, { method: 'POST', body: JSON.stringify(body) }
    ),
  },

  departments: {
    list: () => fetchJson<Department[]>('/departments'),
  },

  portalSettings: {
    get: () => fetchJson<{ UATTestingVisible: string; RequirementsTrackerVisible: string; MaintenanceModeEnabled: string }>('/portal-settings'),
  },

  users: {
    upsert:  (body: object) => fetchJson<User>('/user/sync', { method: 'POST', body: JSON.stringify(body) }),
    logout:  (body: { email: string; displayName: string }) =>
      fetchJson<{ ok: boolean }>('/user/logout', { method: 'POST', body: JSON.stringify(body) }).catch(() => {}),
  },

  comments: {
    create: (projectId: number, body: {
      commentText:  string;
      authorUserID: number;
      isInternal?:  boolean;
      isQuestion?:  boolean;
      actorEmail?:  string;
      actorName?:   string;
    }) => fetchJson<{ commentID: number; createdAt: string }>(
      `/projects/${projectId}/comments`,
      { method: 'POST', body: JSON.stringify(body) }
    ),
    markAnswered: (projectId: number, commentId: number) =>
      fetchJson<{ message: string }>(`/projects/${projectId}/comments/${commentId}/answer`, { method: 'PATCH' }),
    checkReplies: () =>
      fetchJson<{ message: string }>('/inbound-replies/check', { method: 'POST' }),
  },

  scope: {
    list:   (projectId: number) => fetchJson<ScopeItem[]>(`/projects/${projectId}/scope`),
    create: (projectId: number, body: object) =>
      fetchJson<{ scopeItemID: number }>(`/projects/${projectId}/scope`, { method: 'POST', body: JSON.stringify(body) }),
    update: (scopeItemId: number, body: object) =>
      fetchJson<{ message: string }>(`/scope/${scopeItemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (scopeItemId: number) =>
      fetchJson<{ message: string }>(`/scope/${scopeItemId}`, { method: 'DELETE' }),
  },

  formSettings: {
    get:    () => fetchJson<FormFieldSetting[]>('/form-settings'),
    update: (fields: FormFieldSetting[]) =>
      fetchJson<{ success: boolean }>('/mgmt/form-settings', { method: 'PUT', body: JSON.stringify(fields) }),
  },

  notifications: {
    get:      (userID: number) => fetchJson<NotificationsResponse>(`/notifications?userID=${userID}`),
    markRead: (userID: number, ids?: number[]) =>
      fetchJson<{ success: boolean }>('/notifications/read', {
        method: 'POST',
        body: JSON.stringify(ids ? { ids } : { all: true, userID }),
      }),
  },

  activity: {
    get: (userID: number) => fetchJson<ActivityItem[]>(`/activity?userID=${userID}`),
  },

  uat: {
    createSession: (body: {
      testerName:  string;
      testerEmail: string;
      testerRole:  string;
      track:       'user' | 'admin';
      userId?:     number;
    }) => fetchJson<{ sessionId: number; startedAt: string }>(
      '/uat/sessions', { method: 'POST', body: JSON.stringify(body) }
    ),
    saveResponse: (sessionId: number, body: {
      scenarioId:    string;
      scenarioTitle: string;
      result:        'pass' | 'fail' | 'partial' | 'skipped';
      rating?:       number;
      comments?:     string;
      timeSpentSec?: number;
    }) => fetchJson<{ ok: boolean }>(
      `/uat/sessions/${sessionId}/responses`, { method: 'POST', body: JSON.stringify(body) }
    ),
    submitSession: (sessionId: number, body: {
      overallRating?:   number;
      overallComments?: string;
    }) => fetchJson<{ ok: boolean }>(
      `/uat/sessions/${sessionId}/submit`, { method: 'POST', body: JSON.stringify(body) }
    ),
    getSession: (sessionId: number) =>
      fetchJson<{ session: UATSession; responses: UATResponse[] }>(
        `/uat/sessions/${sessionId}`
      ),
  },

  admin: {
    departments: {
      list:   ()                         => fetchJson<Department[]>('/mgmt/departments'),
      create: (body: object)             => fetchJson<{ departmentID: number }>('/mgmt/departments', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: number, body: object) => fetchJson<{ message: string }>(`/mgmt/departments/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      delete: (id: number)               => fetchJson<{ message: string }>(`/mgmt/departments/${id}`, { method: 'DELETE' }),
    },

    users: {
      list: (params?: { search?: string; role?: string }) => {
        const qs = new URLSearchParams();
        if (params?.search) qs.set('search', params.search);
        if (params?.role)   qs.set('role',   params.role);
        const query = qs.toString() ? `?${qs.toString()}` : '';
        return fetchJson<User[]>(`/mgmt/staff${query}`);
      },
      create:     (body: object)             => fetchJson<{ userID: number }>('/mgmt/staff', { method: 'POST', body: JSON.stringify(body) }),
      update:     (id: number, body: object) => fetchJson<{ message: string }>(`/mgmt/staff/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      delete:     (id: number)               => fetchJson<{ message: string }>(`/mgmt/staff/${id}`, { method: 'DELETE' }),
      bulkUpdate: (updates: { userID: number; appRole: string }[]) =>
        fetchJson<{ updated: number }>('/mgmt/staff/bulk', { method: 'PUT', body: JSON.stringify({ updates }) }),
    },

    projects: {
      update: (id: number, body: object) => fetchJson<{ message: string }>(`/mgmt/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      delete: (id: number)               => fetchJson<{ message: string }>(`/mgmt/projects/${id}`, { method: 'DELETE' }),
      reassignDepartment: (id: number, departmentID: number) =>
        fetchJson<{ message: string; newStatus: string }>(`/mgmt/projects/${id}/reassign-department`, { method: 'POST', body: JSON.stringify({ departmentID }) }),
    },

    audit: {
      list: (params?: { search?: string; dateFrom?: string; dateTo?: string }) => {
        const qs = new URLSearchParams();
        if (params?.search)   qs.set('search',   params.search);
        if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
        if (params?.dateTo)   qs.set('dateTo',   params.dateTo);
        const query = qs.toString() ? `?${qs.toString()}` : '';
        return fetchJson<any[]>(`/mgmt/audit${query}`);
      },
      clearByUsers: (emails: string[]) =>
        fetchJson<{ deleted: number; message: string }>(
          `/mgmt/audit/clear?emails=${emails.map(encodeURIComponent).join(',')}`,
          { method: 'DELETE' }
        ),
    },

    loginHistory: {
      list: (params?: { search?: string; dateFrom?: string; dateTo?: string }) => {
        const qs = new URLSearchParams();
        if (params?.search)   qs.set('search',   params.search);
        if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
        if (params?.dateTo)   qs.set('dateTo',   params.dateTo);
        const query = qs.toString() ? `?${qs.toString()}` : '';
        return fetchJson<LoginHistoryEntry[]>(`/mgmt/login-history${query}`);
      },
    },

    settings: {
      get:              ()             => fetchJson<any>('/mgmt/settings'),
      update:           (body: object) => fetchJson<{ success: boolean }>('/mgmt/settings', { method: 'PUT', body: JSON.stringify(body) }),
      sendTestNotification: ()         => fetchJson<{ message: string; emailResult: string; emailNotificationsEnabled: boolean; testModeActive: boolean }>('/mgmt/settings/test-notification', { method: 'POST' }),
    },

    portalSettings: {
      get:    ()                              => fetchJson<PortalSettings>('/mgmt/portal-settings'),
      update: (body: Partial<PortalSettings>) =>
        fetchJson<{ success: boolean }>('/mgmt/portal-settings', { method: 'PUT', body: JSON.stringify(body) }),
    },

    formSettings: {
      update: (fields: FormFieldSetting[]) =>
        fetchJson<{ success: boolean }>('/mgmt/form-settings', { method: 'PUT', body: JSON.stringify(fields) }),
    },

    chain: {
      list:   ()                         => fetchJson<ApprovalChainStep[]>('/mgmt/chain'),
      update: (id: number, body: object) => fetchJson<{ message: string }>(`/mgmt/chain/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },

    stats: {
      get: () => fetchJson<AdminStats>('/mgmt/stats'),
    },

    uat: {
      list:   () => fetchJson<{ sessions: any[] }>('/mgmt/uat/sessions'),
      export: () => fetchJson<{ rows: any[]; exportedAt: string }>('/mgmt/uat/export'),
    },
  },
};